import { useMemo, useState } from 'react';
import { useSeoMeta } from '@unhead/react';
import type { NostrEvent } from '@nostrify/nostrify';

import { AppShell } from '@/components/AppShell';
import { PolaroidFeed } from '@/components/PolaroidFeed';
import { PhotoDetail } from '@/components/PhotoDetail';
import { useFollowingPictures } from '@/hooks/useFollowingPictures';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { CURATOR_PUBKEY } from '@/lib/constants';

export default function Feed() {
  const { user } = useCurrentUser();
  const [selectedPhoto, setSelectedPhoto] = useState<NostrEvent | null>(null);

  // User's own following feed (when logged in)
  const {
    data: userFollowData,
    isLoading: isLoadingUserFollow,
    fetchNextPage: fetchNextUserFollow,
    hasNextPage: hasNextUserFollow,
    isFetchingNextPage: isFetchingNextUserFollow,
    isLoadingFollows: isLoadingUserFollows,
    hasFollows: userHasFollows,
  } = useFollowingPictures(user?.pubkey);

  // Curated feed — MK's follow list (used as default for logged-out users
  // and logged-in users who don't have their own follow list)
  const {
    data: curatedData,
    isLoading: isLoadingCurated,
    fetchNextPage: fetchNextCurated,
    hasNextPage: hasNextCurated,
    isFetchingNextPage: isFetchingNextCurated,
    isLoadingFollows: isLoadingCuratorFollows,
  } = useFollowingPictures(CURATOR_PUBKEY);

  // Use user's own follow list if they have one, otherwise fall back to curated
  const useUserFeed = !!user && userHasFollows && !isLoadingUserFollows;

  const data = useUserFeed ? userFollowData : curatedData;
  const isLoading = useUserFeed ? (isLoadingUserFollows || isLoadingUserFollow) : (isLoadingCuratorFollows || isLoadingCurated);
  const fetchNextPage = useUserFeed ? fetchNextUserFollow : fetchNextCurated;
  const hasNextPage = useUserFeed ? hasNextUserFollow : hasNextCurated;
  const isFetchingNextPage = useUserFeed ? isFetchingNextUserFollow : isFetchingNextCurated;

  // Flatten pages and deduplicate by event ID
  const pictures = useMemo(() => {
    const seen = new Set<string>();
    return (data?.pages.flat() ?? []).filter((event) => {
      if (seen.has(event.id)) return false;
      seen.add(event.id);
      return true;
    });
  }, [data?.pages]);

  const pageCount = data?.pages.length ?? 0;

  useSeoMeta({
    title: 'Polaroids',
    description: 'A digital scrapbook for your Nostr photos.',
  });

  return (
    <AppShell>
      <PolaroidFeed
        pictures={pictures}
        isLoading={isLoading}
        hasNextPage={!!hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        fetchNextPage={fetchNextPage}
        pageCount={pageCount}
        onPhotoClick={setSelectedPhoto}
        showFooter
        emptyState={
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="bg-polaroid-white rounded-[3px] p-4 pb-12 border-2 border-dashed border-cream-dark shadow-sm w-48 mx-auto" style={{ transform: 'rotate(-2deg)' }}>
              <div className="aspect-[4/5] flex items-center justify-center bg-cream-dark/50 rounded-sm">
                <span className="text-4xl">📷</span>
              </div>
              <p className="font-hand text-pol-text-secondary text-center mt-2 text-lg">
                {useUserFeed ? 'No photos from friends yet...' : 'No photos yet...'}
              </p>
            </div>
            <p className="text-pol-text-secondary text-sm mt-4">
              {useUserFeed ? 'Your friends haven\'t posted any photos' : 'Be the first to share a moment'}
            </p>
          </div>
        }
      />

      {/* Photo detail overlay */}
      {selectedPhoto && (
        <PhotoDetail
          event={selectedPhoto}
          onClose={() => setSelectedPhoto(null)}
        />
      )}
    </AppShell>
  );
}
